import React, { memo } from 'react';
import type { FunctionComponent } from 'react';
import './AccountStatusTooltip.scss';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { selectCurrentAccounts } from '@store/sessions/selectors';
import { useParamsInterface } from '@store/types';

import {
	DeletedStatus,
	DisabledStatus,
	ExpiredStatus,
	PastDueStatus,
	PausedStatus,
	SpecialCaseStatus,
	UsageExceededStatus,
	RemovedStatus,
} from './components';

interface AccountStatusTooltipContentProps {
	status: string;
}

enum accountStatuses {
	PAST_DUE = 'PastDue',
	USAGE_EXCEEDED = 'UsageExceeded',
	DISABLED = 'Disabled',
	SPECIAL_CASE = 'SpecialCase',
	PAUSED = 'Paused',
	DELETED = 'Deleted',
	EXPIRED = 'Expired',
	NOT_VERIFIED = 'NotVerified',
	REMOVED = 'Removed',
}

export const AccountStatusTooltipContent: FunctionComponent<AccountStatusTooltipContentProps> = memo(({ status }) => {
	const { account } = useParams<useParamsInterface>();
	const accountInfo = useSelector(selectCurrentAccounts).find(item => item.accountName === account);

	if (!accountInfo || !accountInfo.accountStatusInfo) return null;

	const {
		accountStatusInfo: {
			currentAttempts,
			setBy,
			usageExceededOnUtc,
			becomeActiveOnUtc,
			deletedOnUtc,
			disabledOnUtc,
			expiredOnUtc,
			lastSentOnUtc,
			pausedOnUtc,
			removedOnUtc,
		},
	} = accountInfo;

	switch (status) {
		case accountStatuses.PAST_DUE:
			return <PastDueStatus currentAttempts={currentAttempts} lastSentOnUtc={lastSentOnUtc} />;
		case accountStatuses.USAGE_EXCEEDED:
			return (
				<UsageExceededStatus becomeActiveOnUtc={becomeActiveOnUtc} usageExceededOnUtc={usageExceededOnUtc} />
			);
		case accountStatuses.DISABLED:
			return <DisabledStatus disabledOnUtc={disabledOnUtc} />;
		case accountStatuses.SPECIAL_CASE:
			return <SpecialCaseStatus setBy={setBy} />;
		case accountStatuses.PAUSED:
			return <PausedStatus pausedOnUtc={pausedOnUtc} />;
		case accountStatuses.DELETED:
			return <DeletedStatus deletedOnUtc={deletedOnUtc} />;
		case accountStatuses.EXPIRED:
			return <ExpiredStatus expiredOnUtc={expiredOnUtc} />;
		case accountStatuses.REMOVED:
			return <RemovedStatus removedOnUtc={removedOnUtc} />;
		default:
			return null;
	}
});
